import { useState, useEffect } from 'react';
import { SnackbarType } from '../components/snackbar';

// Use a module-level variable to store the snackbar queue
// This allows access from outside React components
let snackbarQueue: (SnackbarType & { id: string })[] = [];
let listeners: Function[] = [];

// Function to notify all listeners of queue changes
const notifyListeners = () => {
  listeners.forEach(listener => listener(snackbarQueue));
};

// Standalone function for showing snackbars
export const showSnackbar = (props: Omit<SnackbarType, 'className'>) => {
  const id = `snackbar-${Date.now()}`;
  const snackbarItem = { ...props, id };
  
  snackbarQueue = [...snackbarQueue, snackbarItem];
  notifyListeners();
  
  // Auto-dismiss if not specified otherwise
  if (props.autoDismiss !== false) {
    const timeout = props.dismissTimeout || 5000; // Default 5 seconds
    setTimeout(() => {
      hideSnackbar(id);
    }, timeout);
  }
  
  return id;
};

// Standalone function for hiding snackbars
export const hideSnackbar = (id: string) => {
  snackbarQueue = snackbarQueue.filter(snackbar => snackbar.id !== id);
  notifyListeners();
};

// React hook for components that need to access the queue
export const useSnackbar = () => {
  const [queue, setQueue] = useState(snackbarQueue);
  
  // Register this component as a listener for queue changes
  useEffect(() => {
    const listener = (newQueue: typeof snackbarQueue) => {
      setQueue([...newQueue]);
    };
    
    listeners.push(listener);
    
    // Initial state
    setQueue([...snackbarQueue]);
    
    // Cleanup
    return () => {
      listeners = listeners.filter(l => l !== listener);
    };
  }, []);
  
  return {
    queue,
    showSnackbar,
    hideSnackbar
  };
};

export default useSnackbar;
